import {
  Component,
  OnInit,
  ViewChildren,
  ViewChild,
  ElementRef,
  HostListener,
  Input,
  EventEmitter,
  Output,
  AfterViewInit,
  ChangeDetectorRef,
  AfterViewChecked,
  OnDestroy,
} from "@angular/core";
import { Observable, Subscription } from 'rxjs';
@Component({
    selector: "source-question",
    templateUrl: "./source-question.component.html",
    styleUrls: ["./source-question.component.scss"],
    standalone: false
})
export class SourceQuestionComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  constructor(private cdr: ChangeDetectorRef, private elementRef: ElementRef) {
    this.onResize();
  }
  @Input("question-preview") questionPreview: string;
  @Input("index-item") indexItem: string;
  @Input("question-index") questionIndex: number;
  @Input("question-hidden-array") questionHideArray: boolean[];
  @Input() expandAllObservable: Observable<void>;
  @Input("head-question") headQuestion: string;
  @Input("ref-type") refType: string;
  @Input("source") source: 'source-bank' | 'source-select';
  @Input("question-title") questionTitle: string;
  @Input("multiple-part") isMultipart: boolean = false;
  @Output() toggleQuestionHideArrayClicked = new EventEmitter<string>();
  @ViewChild('showAnswersButton', { static: false }) showAnswersButton: ElementRef;
  @ViewChild('hideAnswersButton', { static: false }) hideAnswersButton: ElementRef;
  private focusNext: 'show' | 'hide' | null = null;
  widthQuestionDisplay: number;
  private readonly CORRECT_ANSWER_TEXT = "Correct answer";
  private readonly INCORRECT_ANSWER_TEXT = "Incorrect answer";
  private CELLS_PER_ROW = 3;
  private sub: Subscription;

  ngOnInit(): void {
    if (this.expandAllObservable) {
      this.sub = this.expandAllObservable.subscribe(() => {
        this.checkHorizontalScroll();
      });
    }
  }
  ngAfterViewInit() {
    this.onResize();
    this.cdr.detectChanges();
  }
  ngAfterViewChecked(): void {
    if (!this.focusNext) return;
    if (this.focusNext === 'show' && this.showAnswersButton) {
      this.showAnswersButton.nativeElement.focus();
    } else if (this.focusNext === 'hide' && this.hideAnswersButton) {
      this.hideAnswersButton.nativeElement.focus();
    }
    // Reset after applying focus
    this.focusNext = null;
  }
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
  onResize() {
    const element = document.getElementById("setQuestionDiv");
    if (element) {
      const positionInfo = element.getBoundingClientRect();
      this.widthQuestionDisplay = positionInfo.width - 77;
    }
  }

  public get sourceIndexId(): string {
    return this.source + '-' + this.indexItem + '-' + this.questionIndex;
  }

  toggleQuestionHideArray(focusNext: 'show' | 'hide') {
    this.toggleQuestionHideArrayClicked.emit(this.indexItem);

    // Focus management for accessibility - trigger change detection and focus the button
    this.focusNext = focusNext;
    this.cdr.detectChanges();

    this.checkHorizontalScroll();
  }
  // parsing the html file
  displayQuestion(htmlString: string) {
    const domParser = new DOMParser();
    const htmlElement = domParser.parseFromString(htmlString, "text/html");
    const e = htmlElement.querySelector(".stemStyle");
    const child = e.lastElementChild;
    e.removeChild(child);
    this.unhideFormulaImages(e);
    const tableElement = e.querySelectorAll("table");
    tableElement.forEach((el) => {
      el.querySelectorAll("tr > th").forEach((dataTh) => {
        dataTh.className = "fontData";
      });
      el.querySelectorAll("tr > td").forEach((data) => {
        if (
          data.lastElementChild !== null &&
          data.lastElementChild.hasAttribute("align")
        ) {
          data.className = "fontData";
        } else {
          data.className = "whiteSpace";
        }
        data.removeAttribute("width");
      });
      el.style.minWidth = "400px";
      el.removeAttribute("width");
    });

    this.updateRelativeLinksToEZTOrigin(e);
    this.updateRelativeExternalMediaSources(e);

    e.querySelectorAll("iframe").forEach((iframe) => {
      if (!iframe.hasAttribute("title")) {
        iframe.setAttribute("title", `Content for ${this.questionTitle ?? "question"}`);
      }
    });
    return e.outerHTML;
  }
  convertReducedArray(initialArray) {
    return initialArray.reduce(function (result, value, index, array) {
      if (index % 2 === 0) result.push(array.slice(index, index + 2));
      return result;
    }, []);
  }
  displayAnswers(htmlString: string) {
    const domParser = new DOMParser();
    const htmlElement = domParser.parseFromString(htmlString, "text/html");
    const answerElements = htmlElement.querySelector(".stemStyle");
    answerElements.className = "stemStyle answerBlock";
    const refrenceElements = htmlElement.querySelector(".ref");
    const child = answerElements.lastElementChild;
    const totalHead = document.createElement("div");
    totalHead.className = "HeadElement";
    const headingAnswer = document.createElement("div");
    headingAnswer.className = "answer-head";
    headingAnswer.setAttribute("role", "heading");
    headingAnswer.setAttribute("aria-level", this.isMultipart ? '5' : '4');
    headingAnswer.innerHTML = "Answer";
    headingAnswer.id = `answers-label-${this.sourceIndexId}`;
    child.prepend(headingAnswer);
    const tableElement = child.querySelector("table");
    tableElement?.setAttribute("role", "presentation");
    if (tableElement !== null) {
      if (!tableElement.hasAttribute("class")) {
        tableElement.className = "table-padding-0";
        tableElement.querySelectorAll(".mcCell").forEach((mcCellElement, cellIdx) => {
          const spanElement = mcCellElement.getElementsByTagName("span")[0];
          const inputElement = mcCellElement.getElementsByTagName("input")[0]; 
          if (spanElement !== undefined) {
            spanElement.remove();
            const answerSpan = document.createElement("span");
            answerSpan.className = "arrow-color arrow-color-checkbox";
            answerSpan.setAttribute("aria-hidden", "true");
            mcCellElement.appendChild(answerSpan);
            mcCellElement.appendChild(this.createAccessibleAnswerIndicator());
          } else if (inputElement !== undefined) {
            inputElement.remove();
            const checkboxSpan = document.createElement("span");
            checkboxSpan.className = "checkbox-color";
            mcCellElement.appendChild(checkboxSpan);
          } else if (mcCellElement.innerHTML.trim() === '&nbsp;') {
            // If the cell is empty, hide it from assistive technologies. this empty cell comes from api response
            // Only hide every third cell (the first cell in each answer row)
            if (cellIdx % this.CELLS_PER_ROW !== 0) {
              mcCellElement.setAttribute("aria-hidden", "true");
            }
          }
        });
        tableElement.querySelectorAll("tr").forEach((row, idx) => {
          const cells = row.querySelectorAll("td.mcCell");
          if (cells.length >= 3) {
            const iconCell = cells[0];
            const checkboxCell = cells[1];
            const answerTextCell = cells[2];
            // Only add the indicator if there is NO arrow-color or arrow-color-checkbox span
            if (!iconCell.querySelector(".arrow-color, .arrow-color-checkbox")) {
              iconCell.appendChild(this.createAccessibleAnswerIndicator(true));
            }
            // Assign unique id to answer text cell
            const answerId = `${this.source}-display-answer-checkbox-${this.questionIndex}-${idx}`;
            if (!answerTextCell.id) {
              answerTextCell.setAttribute('id', answerId);
            }
            // Find the custom checkbox span
            const checkboxSpan = checkboxCell.querySelector("span.checkbox-color") as HTMLSpanElement | null;
            if (checkboxSpan) {
              this.setAccessibleSpanAttributes(checkboxSpan, {
                role: 'checkbox',
                className: 'checkbox-color',
                labelledBy: answerId,
                checked: false,
                disabled: true
              });
            }
          }
        });
      } else {
        child.querySelectorAll(".mcCellR").forEach((el, idx) => {
          el.querySelector("input").remove();
          // Find the answer cell (next sibling td with class mcCell)
          const row = el.parentElement;
          let answerCell = null;
          if (row) {
            answerCell = row.querySelector('.mcCell');
          }
          // Find the innermost <td> with the answer text
          // Set role="presentation" on the inner table if present
          let answerTextTd = answerCell;
          if (answerCell) {
            const innerTable = answerCell.querySelector('table');
            const innerTd = answerCell.querySelector('td');
            if (innerTd) answerTextTd = innerTd;
            if (innerTable) innerTable.setAttribute('role', 'presentation');
          }
          // Create a unique id for the answer text
          const answerId = `${this.source}-display-answer-${this.questionIndex}-${idx}`;
          if (answerTextTd) {
            answerTextTd.setAttribute('id', answerId);
          }
          const hasSpan = el.querySelectorAll("span");
          const span = document.createElement("span");
          if (hasSpan.length > 0) {
            hasSpan.forEach((elm) => elm.remove());
            const answerSpan = document.createElement("span");
            answerSpan.className = "arrow-color arrow-color-radio";
            answerSpan.setAttribute("aria-hidden", "true");
            el.appendChild(answerSpan);
            el.appendChild(this.createAccessibleAnswerIndicator());
            this.setAccessibleSpanAttributes(span, {
              role: 'radio',
              className: 'checkbox-span',
              labelledBy: answerId,
              checked: false,
              disabled: true
            });
          } else {
            el.appendChild(this.createAccessibleAnswerIndicator(true));
            this.setAccessibleSpanAttributes(span, {
              role: 'radio',
              className: 'checkbox-span no-answer',
              labelledBy: answerId,
              checked: false,
              disabled: true
            });
          }
          el.appendChild(span);
        });
      }
    }
    totalHead.appendChild(child);
    if (tableElement == null) {
      const dataHead = totalHead.querySelector(".rspStyle") as HTMLElement;
      dataHead.className = "rspStyle left-padding";
    }
    const feedbackDiv = child.querySelector(".feedback_block");
    if (feedbackDiv !== null) {
      feedbackDiv.className = "table-display-div";
      const tablesInFeedback = feedbackDiv.querySelectorAll("table");
      tablesInFeedback.forEach((table) => {
        table.setAttribute("role", "presentation");
      });
      this.unhideFormulaImages(feedbackDiv);
    }
    const refrenceDiv = document.createElement("div");
    refrenceDiv.className = "refrencesDiv container-fluid";
    const headingRefrence = document.createElement("div");
    headingRefrence.className = "headingRefrence";
    headingRefrence.setAttribute("role", "heading");
    headingRefrence.setAttribute("aria-level", this.isMultipart ? '5' : '4');
    headingRefrence.innerHTML = "References";
    refrenceDiv.appendChild(headingRefrence);
    let refrenceDivData = document.createElement("div");
    refrenceElements.childNodes.forEach((refrenceChilds) => {
      let initialNodeArray = [];
      refrenceChilds.childNodes.forEach((val) => {
        if (
          val.nodeName != "BR" &&
          val.textContent.trim() != "" &&
          val.textContent != null
        ) {
          initialNodeArray.push(val);
        }
      });
      initialNodeArray = this.convertReducedArray(initialNodeArray);
      initialNodeArray.map((nodePairArray) => {
        let rowDiv = document.createElement("div");
        rowDiv.className = "row rowRefrencediv";
        if (nodePairArray.length == 1) {
          let refrenceDivChildData = document.createElement("div");
          refrenceDivChildData.className =
            "col-lg-12 col-md-12 refrenceDivData panel-left";
          refrenceDivChildData.textContent = nodePairArray[0].textContent;
          rowDiv.appendChild(refrenceDivChildData);
        } else {
          nodePairArray.map((val, index) => {
            let refrenceDivChildData = document.createElement("div");
            if (index % 2 == 0) {
              refrenceDivChildData.className =
                "col-lg-6 col-md-6 refrenceDivData panel-left";
            } else {
              refrenceDivChildData.className =
                "col-lg-6 col-md-6 refrenceDivData panel-right";
            }
            refrenceDivChildData.textContent = val.textContent;
            rowDiv.appendChild(refrenceDivChildData);
          });
        }
        refrenceDivData.appendChild(rowDiv);
      });
    });
    refrenceDiv.appendChild(refrenceDivData);
    totalHead.appendChild(refrenceDiv);

    this.updateRelativeExternalMediaSources(totalHead);
    this.updateRelativeLinksToEZTOrigin(totalHead);
    
    return totalHead.outerHTML;
  }
  private createAccessibleAnswerIndicator(isIncorrect: boolean = false): HTMLSpanElement {
    const answerTextSpan = document.createElement("span");
    answerTextSpan.className = "sr-only";
    answerTextSpan.textContent = isIncorrect ? this.INCORRECT_ANSWER_TEXT : this.CORRECT_ANSWER_TEXT;
    return answerTextSpan;
  }
  private setAccessibleSpanAttributes(
    span: HTMLSpanElement,
    options: {
      role: 'radio' | 'checkbox',
      className: string,
      labelledBy: string,
      checked?: boolean,
      disabled?: boolean
    }
  ) {
    span.className = options.className;
    span.setAttribute('role', options.role);
    span.setAttribute('aria-labelledby', options.labelledBy);
    span.setAttribute('aria-checked', String(options.checked ?? false));
    span.setAttribute('aria-disabled', String(options.disabled ?? true));
    span.setAttribute('tabindex', '-1');
  }
  /**
   * Determines if the content overflows horizontally.
   * If so, assigns a tabindex to make the horizontal scrollbar focusable;
   * otherwise, removes the tabindex.
   */
  private checkHorizontalScroll(): void {
    const dataHead = this.elementRef.nativeElement.querySelector(".rspStyle") as HTMLElement;
    if (dataHead) {
      if (dataHead.scrollWidth > dataHead.clientWidth) {
        dataHead.tabIndex = 0;
        dataHead.setAttribute('role', 'region');
      } else {
        dataHead.removeAttribute("tabIndex");
        dataHead.removeAttribute('role');
      }
      const tableDiv = dataHead.querySelector(".table-display-div") as HTMLElement;
      if (tableDiv) {
        if (tableDiv.scrollWidth > tableDiv.clientWidth) {
          tableDiv.tabIndex = 0;
          tableDiv.setAttribute('role', 'region');
        } else {
          tableDiv.removeAttribute("tabIndex");
          tableDiv.removeAttribute('role');
        }
      }
    }
  }

  /**
   * Content can contain MathML formulas authored as a <math> element paired with a hidden
   * fallback <img style="display:none;">. When MathML cannot/should not be rendered, the
   * <math> element is removed and its paired image is unhidden instead. A stem or answer
   * can contain several such formula pairs, so every <math> element is processed (not just
   * the first), each one matched to the <img> within its own parent element.
   * @param container the HTML element to scan for <math>/<img> formula pairs
   */
  private unhideFormulaImages(container: Element): void {
    container.querySelectorAll("math").forEach((mathEl) => {
      const imageHTML = mathEl.parentElement?.querySelector("img") ?? null;
      if (imageHTML !== null) {
        imageHTML.style.removeProperty("display");
      }
      mathEl.remove();
    });
  }

  /**
   * Updates only relative <img> links in the provided HTML element to be absolute links that reference external media
   * based on the EZT origin stored in localStorage.
   * @param element the HTML element to update  relative img src tags in
   */
  private updateRelativeExternalMediaSources(element: Element): void {
      element.querySelectorAll("img").forEach((val, index) => {
      if (!val.src.startsWith("//") && val.src.includes("/extMedia")) {
        val.src = localStorage.getItem("eztUrl") + val.attributes["src"].nodeValue;
      }
    });
  
  }
  /**
 * Updates only relative <a> links in the provided HTML element to be absolute links
 * based on the EZT origin stored in localStorage.
 * @param e The HTML element to update links in
 */
private updateRelativeLinksToEZTOrigin(e: Element): void {
  const eztUrl = localStorage.getItem("eztUrl") || "";
  e.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href");
    if (
      href && this.isRelativeUrl(href)) {
      a.setAttribute("href", eztUrl + href);
    }
  });
}

/**
 * Determines if a given URL is relative.
 * Attempts to construct a URL object; if it fails, the URL is considered relative.
 * 
 * @param url The URL string to check.
 * @returns {boolean} True if the URL is relative, false if it is absolute.
 */
private isRelativeUrl(url): boolean {
 return !URL.parse(url);
}

}
